/**
 * @fileoverview Draggable table header cell with optional filter badge display.
 * @module shared/components/ui/DraggableTableHead
 */

import { ReactElement } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { cn } from '@/shared/utils/cn'
import { TableHead } from '@/components/ui/table'
import { Badge } from '@/shared/components/ui'
import { X } from 'lucide-react'

export interface ColumnFilter {
  value: number
  direction: 'lt' | 'gt'
}

export interface DraggableTableHeadProps extends React.ComponentProps<typeof TableHead> {
  /** Unique identifier for drag-and-drop */
  id: string
  /** Current active filter to display as badge */
  currentFilter?: ColumnFilter | undefined
  /** Callback to clear the filter */
  onClearFilter?: (() => void) | undefined
  /** Custom formatter for filter value display */
  formatValue?: ((value: number) => React.ReactNode) | undefined
  /** Insertion indicator edge shown while a shelf chip hovers this header */
  dropEdge?: 'left' | 'right' | null | undefined
}

/**
 * Draggable table header cell with optional filter badge. The whole cell is
 * the drag activator (no separate grip handle); inner controls opt out by
 * stopping pointer/key event propagation. Integrates with @dnd-kit for
 * column reordering.
 *
 * @example
 * ```tsx
 * <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
 *   <SortableContext items={columnOrder}>
 *     <DraggableTableHead
 *       id="sales"
 *       className="text-right"
 *       currentFilter={{ value: 1000, direction: 'gt' }}
 *       onClearFilter={() => clearFilter('sales')}
 *       formatValue={(v) => `$${v}`}
 *     >
 *       <TableHeaderMenu ... />
 *     </DraggableTableHead>
 *   </SortableContext>
 * </DndContext>
 * ```
 */
export function DraggableTableHead({
  id,
  children,
  className,
  currentFilter,
  onClearFilter,
  formatValue,
  dropEdge,
  ...props
}: DraggableTableHeadProps): ReactElement {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })

  // The filter popover is portaled to <body> but bubbles events through the
  // React tree, so its pointer-downs reach this cell's drag activator. Only
  // forward pointer-downs whose DOM target actually lives inside the cell —
  // dropping the portaled ones here (instead of stopPropagation in the
  // popover) keeps Radix's outside-click dismissal working.
  const dragListeners = {
    ...listeners,
    onPointerDown: (event: React.PointerEvent<HTMLTableCellElement>): void => {
      const target = event.target
      if (target instanceof Node && !event.currentTarget.contains(target)) return
      listeners?.['onPointerDown']?.(event)
    },
  }

  const style: React.CSSProperties = {
    // While a DragOverlay ghost is rendered for the active header, dnd-kit
    // swaps this transform from pointer-follow to slot displacement — the
    // dimmed source cell previews where the column will land.
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 1 : undefined,
    position: 'relative',
    touchAction: 'none',
  }

  return (
    <TableHead
      ref={setNodeRef}
      style={style}
      // The entire cell is the drag activator — plain clicks pass through
      // thanks to the PointerSensor's 8px activation distance, and the
      // header-menu trigger stops its own pointer/key events so opening the
      // menu never doubles as a drag start.
      {...attributes}
      {...dragListeners}
      // dnd-kit's attributes set role="button"; restore the th's native
      // table semantics for screen readers.
      role="columnheader"
      className={cn(
        'group relative cursor-grab active:cursor-grabbing',
        className,
        isDragging && 'bg-muted'
      )}
      {...props}
    >
      {/* Insertion indicator while a shelf chip hovers this header */}
      {dropEdge && (
        <span
          data-testid="drop-indicator"
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-y-1 z-30 w-[3px] rounded-full bg-ring shadow-[0_0_10px_2px] shadow-ring/60',
            dropEdge === 'left' ? 'left-0' : 'right-0'
          )}
        />
      )}

      {children}

      {/* Active filter pill — rendered in normal flow under the title block
          (never overlaid on it), right-aligned to match the numeric columns.
          The cell grows a little taller while a filter is active. */}
      {currentFilter && formatValue && (
        <div className="flex justify-end pr-4 pb-2 -mt-1.5">
          <Badge
            variant="secondary"
            className="h-6 gap-1 pl-2 pr-0.5 cursor-default shrink-0 bg-muted/50 hover:bg-muted/80"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="text-xs font-normal whitespace-nowrap">
              {currentFilter.direction === 'lt' ? '<' : '>'} {formatValue(currentFilter.value)}
            </span>
            <div
              role="button"
              tabIndex={0}
              aria-label="Clear filter"
              className="rounded-full p-1 hover:bg-foreground/10 cursor-pointer transition-colors"
              onClick={(e) => {
                e.stopPropagation()
                onClearFilter?.()
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation()
                  onClearFilter?.()
                }
              }}
            >
              <X className="h-3.5 w-3.5" />
            </div>
          </Badge>
        </div>
      )}
    </TableHead>
  )
}
